import { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { useColorStack } from "app/custom-hooks/useColorStack";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { CHART_COLORS } from "app/constants/ChartColors";

import type { ITransformedSerpItem } from "./serpDataTransform";
import { transformSerpFeaturesDistribution } from "./serpDataTransform";
import { SERPFeaturesComposition } from "./SERPFeaturesComposition";
import type { ISerpDistributionItem } from "./types";
import { translate } from "app/filters/i18nFilter";

const SERP_FEATURES_ENDPOINT = "api/WebsiteAnalysis/Overview/KeywordsSerpDistribution";
interface SerpDistributionResponse {
    serpDistribution: ISerpDistributionItem[];
}

export const SERPFeaturesCompositionContainer = () => {
    const { params } = useCurrentStateAndParams();
    const { country, duration, webSource, key: keys, pageFilter: pageFilterJson } = params;

    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
    const apiParams = {
        country,
        webSource,
        from,
        to,
        isWindow,
        keys,
        pageFilterJson,
    };

    const { acquire, release, reset } = useColorStack(CHART_COLORS.chartMainColors);
    const [serpData, setSerpData] = useState<ITransformedSerpItem[]>([]);

    const {
        data,
        isLoading: serpIsLoading,
        isError: serpIsError,
    } = useQuery<SerpDistributionResponse>({
        queryKey: [SERP_FEATURES_ENDPOINT, apiParams],
        queryFn: () => DefaultFetchService.getInstance().get(SERP_FEATURES_ENDPOINT, apiParams),
    });

    useEffect(() => {
        if (!serpIsLoading && !serpIsError && data?.serpDistribution?.length) {
            reset();
            const transformedData = transformSerpFeaturesDistribution(data.serpDistribution, acquire);
            setSerpData(transformedData);
        } else {
            setSerpData([]);
        }
    }, [data, serpIsLoading, serpIsError, reset, acquire]);

    const hasData = data?.serpDistribution?.some((item) => Object.keys(item?.serpFeatures || {}).length > 0) || false;

    return (
        <SERPFeaturesComposition
            data={serpData}
            isLoading={serpIsLoading}
            isError={serpIsError}
            hasData={hasData}
            tooltip={translate("website.analysis.seo.overview.serp_features_composition.subtitle")}
            acquire={acquire}
            release={release}
        />
    );
};
